import { Navigate, matchPath, useLocation } from "react-router-dom";
import ModalView, { ModalElementProps } from "src/components/layout/ModalView";
import useTenant from "src/hooks/api/services/tenants/useTenant";
import useAuth from "src/hooks/selectors/useAuth";
import useSubscription from "src/hooks/selectors/useSubscription";
import { getRedirectUrl } from "src/hooks/useRedirectUrl";
import RedirectAuth from "src/routes/middlewares/RedirectAuth";
import BlockedAccount from "src/views/auth/BlockedAccount";
import NotFoundView from "src/views/auth/NotFoundView";

interface ProtectedRouteProps {
	type?: "student" | "company";
	guard?: string[];
	variant?: "modal" | "on-top";
	children?: any;
	modal?: ModalElementProps;
}

const ProtectedRoute = ({
	type,
	guard,
	variant,
	children,
	modal,
}: ProtectedRouteProps) => {
	const auth = useAuth();
	const location: any = useLocation();
	// const isMatched = useRouteMatch(rest?.path);
	// console.log(matchPath("/payments/:id/refresh", location.pathname));
	const languageUrl = getRedirectUrl("/language", location.pathname);
	const subscription = useSubscription();
	const isChargeback = subscription?.payment?.status === "chargeback";
	const refreshPath = `/payments/${subscription?.payment?.hid}/refresh`;
	const BLOCKED_STATUSES = ["terminated", "blocked", "suspended"];
	const { onboarding_status } = auth;
	const { tenant } = useTenant();
	const modules = tenant?.modules || [];

	if (!auth?.id) {
		return <RedirectAuth />;
	}

	if (auth.id && BLOCKED_STATUSES.includes(onboarding_status)) {
		return <BlockedAccount />;
	}
	if (
		auth.type === "company" &&
		isChargeback &&
		!matchPath("/payments/:id/refresh", location.pathname)
	) {
		return <Navigate to={refreshPath} />;
	}

	if (
		auth?.id &&
		!tenant.has_access &&
		!matchPath("/enable-access", location.pathname)
	) {
		return <Navigate to="/enable-access" />;
	}

	if (
		auth.id &&
		!auth?.language &&
		!matchPath("/language", location.pathname)
	) {
		return <Navigate to={languageUrl} />;
	}

	const isNotAllowed = type && type !== auth.type;
	if (isNotAllowed) {
		return <Navigate to="/dashboard" />;
	}

	if (
		guard?.length &&
		!guard.every((middleware: any) => modules.includes(middleware))
	) {
		return <NotFoundView />;
	}

	if (variant === "modal" && modal) {
		return (
			<ModalView {...{ modal }} visible={true}>
				{children}
			</ModalView>
		);
	}

	if (variant === "on-top") {
		return <>{children}</>;
	}

	return children;
};

ProtectedRoute.defaultProps = {
	guard: [],
};

export default ProtectedRoute;
